import { useMemo } from 'react';

import type { DxfMappingSubject } from 'modules/common/viewer/dxfViewer/mapping/mappingActions';
import type { MappingValidationState } from 'modules/common/viewer/dxfViewer/session/mappingValidation';
import type { DxfViewerSession } from 'modules/common/viewer/dxfViewer/session/types';
import { useReadableStore } from 'modules/common/viewer/dxfViewer/session/useSessionState';

const toValidationSubject = (
  validation: MappingValidationState,
): DxfMappingSubject['validation'] => {
  if (validation.error !== null) {
    return { status: 'failed' };
  }
  const issueCodes = validation.issues.map((issue) => issue.code);
  return !validation.isInitialized || validation.isPending
    ? { status: 'pending', issueCodes }
    : { status: 'checked', issueCodes };
};

/**
 * The session's mapping as a `dxfMapping` action target, or `null` before a file
 * is attached. Re-renders on mapping and validation changes, never on pan/zoom.
 */
export const useDxfMappingSubject = (
  session: DxfViewerSession,
): DxfMappingSubject | null => {
  const partFileUuid = useReadableStore(session.partFileUuid);
  const hasSaved = useReadableStore(
    session.mapping,
    (current) => current.saved !== null,
  );
  const hasUnsavedChanges = useReadableStore(session.hasUnsavedMapping);
  const validation = useReadableStore(session.mappingValidation);

  return useMemo(
    () =>
      partFileUuid
        ? {
            partFileUuid,
            hasSaved,
            hasUnsavedChanges,
            validation: toValidationSubject(validation),
          }
        : null,
    [partFileUuid, hasSaved, hasUnsavedChanges, validation],
  );
};
